<script setup lang="ts">
import { NuxtLink } from '#components'

import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from '@/components/shadcn-ui/sidebar'

import {
  PieChart,
  FrameIcon,
  BriefcaseBusinessIcon,
  BotIcon,
  ShieldIcon,
  NotebookTabsIcon,
} from 'lucide-vue-next'
import NavUser from './SidebarUser.vue'

import { useUser } from '~/stores/user/user'
import { usePartners } from '~/stores/partners/partners'
import type { IAppSidebar } from '~/components/layouts/sidebar/types'
import CollapsibleNavGroup from '~/components/layouts/sidebar/CollapsibleNavGroup.vue'
import { Routes } from '~/constants/routes'
import type { IPartners } from '~/gateway/partners/partners.types'

const route = useRoute()
const { state: user, actions: userActions, getters: userGetters } = useUser()
const { state: partners, actions: partnersActions } = usePartners<typeof userGetters.role>()

await partnersActions.fetch()

const selectedPartner = ref<IPartners.BaseModel | null>(null)

const isRouteActive = (url: string) => {
  return route.path === url
}

const isPartnerActive = (partnerSlug: string) => {
  return route.path.includes(`/partners/${partnerSlug}`)
}

const partnersNavGroup = computed<IAppSidebar.NavGroup>(() => ({
  title: 'Partners',
  icon: FrameIcon,
  isActive: true,
  items: partners.value.map(partner => ({
    title: partner.name,
    url: `${Routes.PartnerDetailsLeads(partner.slug)}`,
    icon: BriefcaseBusinessIcon,
    isActive: isPartnerActive(partner.slug),
  })),
}))

const selectedPartnerNavGroup = computed<IAppSidebar.NavGroup>(() => ({
  title: `${selectedPartner.value?.name} Workspace`,
  icon: BotIcon,
  isActive: !!selectedPartner.value,
  items: [
    // {
    //   title: 'Dashboard',
    //   url: `${Routes.PartnerDetails(selectedPartner.value?.slug as string)}`,
    //   isActive: isRouteActive(`${Routes.PartnerDetails(selectedPartner.value?.slug as string)}`),
    // },
    {
      title: 'Leads',
      url: `${Routes.PartnerDetailsLeads(selectedPartner.value?.slug as string)}`,
      isActive: isRouteActive(
        `${Routes.PartnerDetailsLeads(selectedPartner.value?.slug as string)}`,
      ),
    },
    {
      title: 'Events',
      url: `${Routes.PartnerDetailsLogs(selectedPartner.value?.slug as string)}`,
      isActive: isRouteActive(
        `${Routes.PartnerDetailsLogs(selectedPartner.value?.slug as string)}`,
      ),
    },
    {
      title: 'Settings',
      url: `${Routes.PartnerDetailsSettings(selectedPartner.value?.slug as string)}`,
      isActive: isRouteActive(
        `${Routes.PartnerDetailsSettings(selectedPartner.value?.slug as string)}`,
      ),
    },
  ],
}))

const administrationNavGroup: IAppSidebar.NavGroup = {
  title: 'Administration',
  icon: ShieldIcon,
  items: [
    {
      title: 'Partners Management',
      url: Routes.Partners,
      isActive: isRouteActive(Routes.Partners),
    },
    {
      title: 'User Management',
      url: Routes.Users,
      isActive: isRouteActive(Routes.Users),
    },
  ],
}

const dashboardNavItem = computed<IAppSidebar.NavItem>(() => ({
  title: 'Dashboard',
  url: Routes.Home,
  icon: PieChart,
  isActive: isRouteActive(Routes.Home),
}))

const leadsNavItem = computed<IAppSidebar.NavItem>(() => ({
  title: 'Leads',
  url: Routes.LeadsList,
  icon: NotebookTabsIcon,
  isActive: isRouteActive(Routes.LeadsList),
}))

watch(
  () => route.path,
  newPath => {
    const partner = partners.value.find(partner => newPath.includes(partner.slug))
    if (partner) {
      selectedPartner.value = partner as IPartners.BaseModel
    } else {
      if (!userGetters.isAdmin) {
        selectedPartner.value = (partners.value[0] as IPartners.BaseModel) || null
      } else {
        selectedPartner.value = null
      }
    }
  },
  { immediate: true },
)
</script>

<template>
  <Sidebar>
    <SidebarHeader> Partner Connector </SidebarHeader>
    <SidebarContent>
      <SidebarGroup v-if="userGetters.isAdmin" class="group-data-[collapsible=icon]:hidden">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton as-child :is-active="dashboardNavItem.isActive">
              <NuxtLink :to="dashboardNavItem.url">
                <component :is="dashboardNavItem.icon" />
                <span>{{ dashboardNavItem.title }}</span>
              </NuxtLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton as-child :is-active="leadsNavItem.isActive">
              <NuxtLink :to="leadsNavItem.url">
                <component :is="leadsNavItem.icon" />
                <span>{{ leadsNavItem.title }}</span>
              </NuxtLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>
      <CollapsibleNavGroup :group="partnersNavGroup" />
      <CollapsibleNavGroup v-if="selectedPartner" :group="selectedPartnerNavGroup" />
      <CollapsibleNavGroup v-if="userGetters.isAdmin" :group="administrationNavGroup" />
    </SidebarContent>
    <SidebarFooter>
      <NavUser :user="user" @sign-out="userActions.signOut" />
    </SidebarFooter>
    <SidebarRail />
  </Sidebar>
</template>
